import React, { useState, useCallback } from 'react';
import { Sword, Shield, Target } from 'lucide-react';

interface RockPaperScissorsProps {
  difficulty: 'easy' | 'medium' | 'hard';
  onWin: () => void;
  onLose: () => void;
  onDraw: () => void;
}

type Choice = 'rock' | 'paper' | 'scissors' | null;

export function RockPaperScissors({ difficulty, onWin, onLose, onDraw }: RockPaperScissorsProps) {
  const [playerChoice, setPlayerChoice] = useState<Choice>(null);
  const [aiChoice, setAiChoice] = useState<Choice>(null);
  const [result, setResult] = useState<string>('');
  const [isPlaying, setIsPlaying] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(false);

  const choices: Choice[] = ['rock', 'paper', 'scissors'];

  const getIcon = (choice: Choice) => {
    switch (choice) {
      case 'rock': return <Sword className="w-16 h-16" />;
      case 'paper': return <Shield className="w-16 h-16" />;
      case 'scissors': return <Target className="w-16 h-16" />;
      default: return null;
    }
  };

  const getChoiceName = (choice: Choice) => {
    switch (choice) {
      case 'rock': return 'Sword';
      case 'paper': return 'Shield';
      case 'scissors': return 'Bow';
      default: return '';
    }
  };

  const determineWinner = useCallback((player: Choice, ai: Choice) => {
    if (!player || !ai) return '';
    if (player === ai) {
      onDraw();
      return 'Draw!';
    }
    if (
      (player === 'rock' && ai === 'scissors') ||
      (player === 'paper' && ai === 'rock') ||
      (player === 'scissors' && ai === 'paper')
    ) {
      onWin();
      return 'You Win!';
    }
    onLose();
    return 'AI Wins!';
  }, [onWin, onLose, onDraw]);

  const getAIChoice = useCallback((playerHistory: Choice[]): Choice => {
    if (difficulty === 'easy') {
      return choices[Math.floor(Math.random() * choices.length)];
    }

    // For medium/hard, use pattern recognition
    if (playerHistory.length < 2) {
      return choices[Math.floor(Math.random() * choices.length)];
    }

    const lastChoice = playerHistory[playerHistory.length - 1];
    const patternCount = {
      rock: 0,
      paper: 0,
      scissors: 0
    };

    playerHistory.forEach(choice => {
      if (choice) patternCount[choice]++;
    });

    // Counter the most used choice
    const maxCount = Math.max(...Object.values(patternCount));
    const favoredChoice = Object.entries(patternCount)
      .find(([_, count]) => count === maxCount)?.[0] as Choice;

    if (favoredChoice) {
      switch (favoredChoice) {
        case 'rock': return 'paper';
        case 'paper': return 'scissors';
        case 'scissors': return 'rock';
      }
    }

    return choices[Math.floor(Math.random() * choices.length)];
  }, [difficulty]);

  const handleChoice = useCallback((choice: Choice) => {
    if (isPlaying || !choice) return;
    
    setIsPlaying(true);
    setPlayerChoice(choice);
    setAiChoice(null);
    setResult('');
    setShowResult(false);
    
    // Start 3-second countdown
    setCountdown(3);
    
    const countdownInterval = setInterval(() => {
      setCountdown(prev => {
        if (prev === null || prev <= 1) {
          clearInterval(countdownInterval);
          const aiMove = getAIChoice([choice]);
          setAiChoice(aiMove);
          
          // Show result after 3 seconds
          setTimeout(() => {
            const finalResult = determineWinner(choice, aiMove);
            setResult(finalResult);
            setShowResult(true);
            setIsPlaying(false);
          }, 3000);
          
          return null;
        }
        return prev - 1;
      });
    }, 1000);
  }, [isPlaying, getAIChoice, determineWinner]);

  if (!isPlaying && !playerChoice) {
    return (
      <div className="text-center space-y-6">
        <h2 className="text-2xl font-bold text-white mb-4">Choose Your Weapon</h2>
        <div className="flex justify-around w-full">
          {choices.map((choice) => (
            <button
              key={choice}
              onClick={() => handleChoice(choice)}
              className="p-6 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors group"
            >
              <div className="text-white mb-4 group-hover:scale-110 transition-transform">
                {getIcon(choice)}
              </div>
              <div className="text-gray-300 text-lg font-medium">{getChoiceName(choice)}</div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-8">
      <div className="grid grid-cols-2 gap-16 w-full max-w-2xl">
        <div className="text-center">
          <h3 className="text-xl font-bold text-cyan-400 mb-6">Your Choice</h3>
          <div className="h-32 flex flex-col items-center justify-center bg-gray-800 rounded-lg p-4">
            {playerChoice && (
              <div className="text-cyan-400 transition-all duration-500">
                {getIcon(playerChoice)}
                <div className="mt-4 text-lg font-medium">{getChoiceName(playerChoice)}</div>
              </div>
            )}
          </div>
        </div>

        <div className="text-center">
          <h3 className="text-xl font-bold text-pink-500 mb-6">AI Choice</h3>
          <div className="h-32 flex flex-col items-center justify-center bg-gray-800 rounded-lg p-4">
            {countdown !== null ? (
              <div className="text-4xl font-bold text-yellow-400 animate-pulse">
                {countdown}
              </div>
            ) : aiChoice && (
              <div className="text-pink-500 animate-fade-in">
                {getIcon(aiChoice)}
                <div className="mt-4 text-lg font-medium">{getChoiceName(aiChoice)}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="text-3xl font-bold mb-4">
        {showResult && result && (
          <span className={
            result.includes('Win') ? 'text-green-400' : 
            result === 'Draw!' ? 'text-blue-400' : 
            'text-red-400'
          }>
            {result}
          </span>
        )}
      </div>

      {showResult && (
        <button
          onClick={() => {
            setPlayerChoice(null);
            setAiChoice(null);
            setResult('');
            setCountdown(null);
            setShowResult(false);
          }}
          className="px-8 py-3 bg-gradient-to-r from-yellow-600 to-amber-500 text-white rounded-lg hover:from-yellow-500 hover:to-amber-400 text-lg font-medium"
        >
          Play Again
        </button>
      )}
    </div>
  );
}